import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import type { ApiFailure, ApiResult } from '@/api/client'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { INTERVALS, OFF, PAUSED, describeInterval, optionsFor, valueOf } from './intervals'
import type { Monitor } from './intervals'

/** What a submit asks for: stop the monitor, or set it to a state and interval. */
export type UptimeChoice =
  { kind: 'off' } | { kind: 'set'; url: string; enabled: boolean; seconds: number }

/**
 * One site's uptime checks, as one form used by the Settings panel and the
 * wizard step.
 *
 * Settings consolidation, batch 2 (2026-09-15): moved from `pages/seo/Sites`,
 * where the same three-way control saved on every change in the last column
 * of the uptime table. *Off* and *Paused* both stop the pings and they are not
 * the same thing — `DELETE` discards the monitor's history and its interval,
 * `PUT enabled: false` keeps both — which is why the caption under the control
 * says so; the options stay one word each so the select does not clip.
 */
export function UptimeForm({
  monitor,
  domain,
  onSubmit,
  submitLabel,
  disabled = false,
}: {
  monitor: Monitor | null
  domain: string
  onSubmit: (choice: UptimeChoice) => Promise<ApiResult<unknown>>
  submitLabel: string
  disabled?: boolean
}): JSX.Element {
  const [value, setValue] = useState(valueOf(monitor))
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    const choice: UptimeChoice =
      value === OFF
        ? { kind: 'off' }
        : {
            kind: 'set',
            url: monitor?.url ?? `https://${domain}`,
            enabled: value !== PAUSED,
            seconds: value === PAUSED ? (monitor?.interval_seconds ?? INTERVALS[0]) : Number(value),
          }
    const result = await onSubmit(choice)
    setBusy(false)
    if (!result.ok) setFailure(result.failure)
  }

  return (
    <form
      className="ar-stack"
      aria-label={`Uptime checks — ${domain}`}
      onSubmit={(event) => void submit(event)}
    >
      <Field
        label={`Check ${domain}`}
        help="A plain request from outside AWS on a fixed interval, with no model in the loop. One failed check emails you; one success brings it back. Off forgets the checks and their history; Paused keeps both."
      >
        {(aria) => (
          <select
            {...aria}
            className="ar-select"
            disabled={disabled}
            value={value}
            onChange={(event) => {
              setFailure(null)
              setValue(event.target.value)
            }}
          >
            <option value={OFF}>Off</option>
            <option value={PAUSED}>Paused</option>
            {optionsFor(monitor).map((seconds) => (
              <option key={seconds} value={String(seconds)}>
                {describeInterval(seconds)}
              </option>
            ))}
          </select>
        )}
      </Field>

      <InlineError failure={failure} />

      <div className="ar-row">
        <span className="ar-card__spacer" />
        <Button type="submit" tone="primary" disabled={disabled} busy={busy} busyLabel="Saving">
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
